import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FieldsetModule } from 'primeng/fieldset';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingMasterType, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItem, WarehousingMasterData } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-transfer-form',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, FieldsetModule,
    IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, ProgressBarModule,
    PageFormSectionComponent, PageHeroComponent, PageShellComponent, SelectModule, TableModule, TextareaModule, ToastModule
  ],
  providers: [MessageService],
  templateUrl: './warehousing-transfer-form.component.html'
})
export class WarehousingTransferFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);

  isLoading = false;
  isSaving = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('New Transfer', 'Transfers', '/warehousing/transfers');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  items: WarehousingItem[] = [];
  masterData = masterDataMap();

  transferForm = this.fb.group({
    referenceNumber: [''],
    crmClientId: [null as number | null, Validators.required],
    crmAccountId: [null as number | null, Validators.required],
    sourceRegionId: [null as number | null, Validators.required],
    sourceWarehouseId: [null as number | null, Validators.required],
    destinationRegionId: [null as number | null, Validators.required],
    destinationWarehouseId: [null as number | null, Validators.required],
    scheduledDate: [this.today()],
    notes: [''],
    lines: this.fb.array([this.createLineGroup()])
  });

  get lines(): FormArray {
    return this.transferForm.get('lines') as FormArray;
  }

  itemOptions(): (WarehousingItem & { displayLabel: string })[] {
    return this.items.map(item => ({
      ...item,
      displayLabel: [item.itemNumber, item.itemName, itemSizeLabel(item)].filter(Boolean).join(' | ')
    }));
  }

  ngOnInit(): void {
    this.registerHeaderDependencies();
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;
    forkJoin({
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      items: this.warehousingService.getItems(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.items = response.items.data || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = this.companyWarehouses(response.warehouses.data || []);
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  addLine(): void {
    this.lines.push(this.createLineGroup());
  }

  removeLine(index: number): void {
    if (this.lines.length > 1) this.lines.removeAt(index);
  }

  createTransfer(): void {
    if (this.isSaving) {
      return;
    }
    if (this.transferForm.invalid) {
      this.transferForm.markAllAsTouched();
      return;
    }

    const value = this.transferForm.getRawValue();
    this.isSaving = true;
    this.warehousingService.createTransfer({
      referenceNumber: value.referenceNumber || undefined,
      crmClientId: value.crmClientId!,
      crmAccountId: value.crmAccountId!,
      sourceRegionId: value.sourceRegionId!,
      sourceWarehouseId: value.sourceWarehouseId!,
      destinationRegionId: value.destinationRegionId!,
      destinationWarehouseId: value.destinationWarehouseId!,
      scheduledDate: value.scheduledDate || undefined,
      notes: value.notes || undefined,
      lines: (value.lines || []).map(line => ({
        itemId: line['itemId']!,
        quantityRequested: line['quantityRequested']!,
        sourceZoneId: line['sourceZoneId']!,
        sourceConditionId: line['sourceConditionId']!,
        destinationZoneId: line['destinationZoneId']!,
        destinationConditionId: line['destinationConditionId']!
      }))
    }).pipe(finalize(() => this.isSaving = false)).subscribe(resp => {
      if (!resp.success) return;
      this.messageService.add({
        severity: 'success',
        summary: 'Transfer Created',
        detail: resp.data?.transferNumber ? `${resp.data.transferNumber} was created.` : 'Transfer order was created.'
      });
      this.router.navigate(['/warehousing/transfers/view', resp.data?.id]);
    });
  }

  discard(): void {
    this.router.navigate(['/warehousing/transfers']);
  }

  filteredAccounts(clientId: number | null | undefined): CrmAccount[] {
    return this.accounts.filter(account => !clientId || !account.crmClientId || account.crmClientId === clientId);
  }

  options(type: WarehousingMasterType): WarehousingMasterData[] {
    return this.masterData[type] || [];
  }

  warehouseOptions(regionControl: 'sourceRegionId' | 'destinationRegionId'): WarehousingMasterData[] {
    const regionId = this.transferForm.get(regionControl)?.value as number | null;
    return regionId ? this.masterData.WAREHOUSE.filter(row => row.parentId === regionId) : [];
  }

  zoneOptions(warehouseControl: 'sourceWarehouseId' | 'destinationWarehouseId'): WarehousingMasterData[] {
    const warehouseId = this.transferForm.get(warehouseControl)?.value as number | null;
    return warehouseId ? this.masterData.ZONE.filter(row => row.parentId === warehouseId) : [];
  }

  isSourceZoneDisabled(): boolean {
    return !this.transferForm.get('sourceWarehouseId')?.value;
  }

  isDestinationZoneDisabled(): boolean {
    return !this.transferForm.get('destinationWarehouseId')?.value;
  }

  private createLineGroup(): FormGroup {
    return this.fb.group({
      itemId: [null as number | null, Validators.required],
      quantityRequested: [1, [Validators.required, Validators.min(0.0001)]],
      sourceZoneId: [null as number | null, Validators.required],
      sourceConditionId: [null as number | null, Validators.required],
      destinationZoneId: [null as number | null, Validators.required],
      destinationConditionId: [null as number | null, Validators.required]
    });
  }

  private registerHeaderDependencies(): void {
    this.transferForm.get('sourceRegionId')?.valueChanges.subscribe(() => {
      this.transferForm.patchValue({ sourceWarehouseId: null }, { emitEvent: false });
      this.clearLineZones('sourceZoneId');
    });
    this.transferForm.get('destinationRegionId')?.valueChanges.subscribe(() => {
      this.transferForm.patchValue({ destinationWarehouseId: null }, { emitEvent: false });
      this.clearLineZones('destinationZoneId');
    });
    this.transferForm.get('sourceWarehouseId')?.valueChanges.subscribe(() => this.clearLineZones('sourceZoneId'));
    this.transferForm.get('destinationWarehouseId')?.valueChanges.subscribe(() => this.clearLineZones('destinationZoneId'));
  }

  private clearLineZones(controlName: 'sourceZoneId' | 'destinationZoneId'): void {
    this.lines.controls.forEach(control => control.patchValue({ [controlName]: null }, { emitEvent: false }));
  }

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private companyWarehouses(rows: WarehousingMasterData[]): WarehousingMasterData[] {
    return (rows || []).filter(row => row.warehouseOwnershipType !== 'THIRD_PARTY_VIRTUAL');
  }
}
